import { Guid } from 'guid-typescript';
import { DragStates, ShapeKeys } from '../enums';

export class ShapeAttributesManager {

  private static readonly _keysMap = new Map<ShapeKeys, { attribute: string, dataset: string }>([
    [ShapeKeys.DRAG_ID, { attribute: 'data-drag-id', dataset: 'dragId' }],
    [ShapeKeys.DRAG_STATE, { attribute: 'data-drag-state', dataset: 'dragState' }],
    [ShapeKeys.SHAPE_TYPE, { attribute: 'data-shape-type', dataset: 'shapeType' }],
    [ShapeKeys.SHAPE_BACKGROUND_COLOR, { attribute: 'data-background-color', dataset: 'backgroundColor' }],
    [ShapeKeys.BLOCK, { attribute: 'data-block', dataset: 'block' }],
    [ShapeKeys.ACTIVE_EXTERNAL_SELECTION, { attribute: 'data-active-external-selection', dataset: 'activeExternalSelection' }],
    [ShapeKeys.GUID, { attribute: 'data-guid', dataset: 'guid' }]
  ]);

  public static getAttribute(key: ShapeKeys): string {
    return ShapeAttributesManager._keysMap.get(key).attribute;
  }

  public static getDataset(key: ShapeKeys): string {
    return ShapeAttributesManager._keysMap.get(key).dataset;
  }

  /**
   * Get & set GUID
   */

  public static getGUID(el: HTMLElement): string {
    return el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.GUID)];
  }

  public static setGUID(el: HTMLElement, guid?: string): void {
    el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.GUID)] = guid ?? Guid.raw();
  }

  /**
   * Get & set ACTIVE_ROW_SELECTION
   */

  public static getActiveExternalSelection(el: HTMLElement): boolean {
    return +el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.ACTIVE_EXTERNAL_SELECTION)] === 1;
  }

  public static setActiveExternalSelection(el: HTMLElement, isActive: 0 | 1): void {
    el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.ACTIVE_EXTERNAL_SELECTION)] = `${isActive}`;
  }

  /**
   * Get & set SHAPE ID
   */

  public static getShapeId(el: HTMLElement): number {
    return +el.id;
  }

  public static setShapeId(el: HTMLElement, id: string): void {
    el.id = id;
  }

  /**
   * Get & set DRAG_STATE
   */

  public static getDragState(el: HTMLElement): string {
    return el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.DRAG_STATE)];
  }

  public static setDragState(el: HTMLElement, state: DragStates): void {
    el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.DRAG_STATE)] = state;
  }

  /**
   * Get & set DRAG_ID
   */

  public static getDragId(el: HTMLElement): string {
    return el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.DRAG_ID)];
  }

  public static setDragId(el: HTMLElement, id?: string): void {
    el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.DRAG_ID)] = id ?? Guid.raw();
  }

  /**
   * Get & set SHAPE_TYPE
   */

  public static getShapeType(el: HTMLElement): string {
    return el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.SHAPE_TYPE)];
  }

  public static setShapeType(el: HTMLElement, type: string): void {
    el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.SHAPE_TYPE)] = type;
  }

  /**
   * Get & set SHAPE BACKGROUND COLOR
   */

  public static getShapeBackgroundColor(el: HTMLElement): string {
    return el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.SHAPE_BACKGROUND_COLOR)];
  }

  public static setShapeBackgroundColor(el: HTMLElement, color: string): void {
    el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.SHAPE_BACKGROUND_COLOR)] = color;
  }

  /**
   * Get & set ALLOW MOVE
   */

  public static getShapeBlockMove(el: HTMLElement): boolean {
    return el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.BLOCK)] === 'true';
  }

  public static setShapeBlockMove(el: HTMLElement, block: boolean): void {
    el.dataset[ShapeAttributesManager.getDataset(ShapeKeys.BLOCK)] = block.toString();
  }
}
